/**
 * Skeleton retargeting — bind a clip authored on a DIFFERENT humanoid skeleton
 * (e.g. Adobe Mixamo) onto our UE5 "Manny" rig (`mannyRig.ts`) so it plays
 * NATIVELY through the same AnimationMixer. Rotation-only (the sim owns
 * translation, like the native path), and a pure per-bone transform, so it slots
 * straight into `normalizeClip`'s existing track loop (see anim.ts).
 *
 * The math is rest-offset retarget with BIND-POSE MATCHING: the TARGET bone takes the
 * SAME orientation RELATIVE TO ITS OWN BIND, expressed in the bone's own (bind-local)
 * frame, as the SOURCE bone has relative to its bind — i.e. Rt⁻¹·Wt == Rs⁻¹·Ws. This is
 * what makes a T-pose-authored clip (Mixamo) look right on an A-pose rig (Manny): the
 * arms land where the source posed them, not 45° lower (preserving the WORLD delta-from-
 * rest instead — the naive model — overshoots by the T-vs-A rest difference and crosses
 * the arms into the body). Per mapped bone, with rest WORLD quaternions Rs/Rps (source
 * bone/parent) and Rt/Rpt (target bone/parent), the target LOCAL quaternion is
 *     Lt(t) = A · Ls(t),   A = Rpt⁻¹·Rt·Rs⁻¹·Rps
 * (a pure left-multiply; B is identity). A is constant per bone — precomputed once, then
 * applied to every keyframe. Gated by retarget.test.mts LAYER 0 + the FK oracle.
 *
 * Frames are reconciled FIRST: the source and target rests must share a global
 * orientation or the world-quaternion offsets above are corrupted. `align` (auto
 * by default) derives an orthonormal (right,up,forward) basis from each skeleton's
 * REST BONE POSITIONS and snaps the source→target rotation to the 90° grid, so a
 * Y-up/Z-up or facing difference is cancelled EXACTLY (real FBX frame differences
 * are always axis-aligned). Only the source ROOT bone's local rotation is frame-
 * dependent, and clips drop the root track, so non-root locals retarget cleanly.
 */
import * as THREE from "three";
import { MANNY_RIG } from "./mannyRig";

/** How to bind a foreign skeleton's clip onto Manny. */
export interface RetargetMap {
  /** Source track-bone name → Manny bone name. Unmapped source bones are dropped
   *  (they stay at the rig's rest, exactly like the native path's foreign bones). */
  bones: Record<string, string>;
  /** Global rest-frame reconciliation. "auto" (default) derives + snaps it from the
   *  rest geometry; pass an explicit quaternion to override the heuristic. */
  align?: THREE.Quaternion | "auto";
  /** Retarget method. "bind" (default) matches each bone's orientation RELATIVE TO ITS OWN
   *  BIND (Lt = A·Ls, B=identity) — right when source and target share a bone-axis convention
   *  (Mixamo↔Manny), and it corrects the T-vs-A rest-pose difference. "world" instead reproduces
   *  the SOURCE's ABSOLUTE WORLD pose on the target (Wt = Ws·K ⟹ Lt = A·Ls·B) — needed when
   *  the source bone LOCAL AXES differ from Manny's (e.g. the Blender/Rigify-derived Quaternius
   *  UAL packs: their +Y-down-the-bone convention makes bind-matching rotate about the wrong
   *  axis, so the rest pose looks right but MOTION tips the body). The constants are taken
   *  against a STANCE-MATCHED target rest (see stanceSwings): each target bone's rest is
   *  re-posed onto the source's rest bone DIRECTION first, so a T-pose source driving an
   *  A-pose target lands the arms exactly where the source posed them — raw world-delta
   *  would apply the T→down swing ON TOP of the already-down A rest and send the arms
   *  past the body ("arms swing behind"). A T-pose clip therefore shows a REAL T. */
  mode?: "bind" | "world";
  /** The source intentionally has no SkinnedMesh and its loaded bone hierarchy is the
   *  authored rest pose. Keep this asset-specific: unknown skinless exports still warn. */
  trustLiveRestWithoutSkin?: boolean;
}

/** A built, clip-ready retargeter (rest snapshot + per-bone constants baked). */
export interface Retargeter {
  /** The resolved source→target frame rotation (identity when frames already match). */
  readonly align: THREE.Quaternion;
  /** Source bone whose `.position` track carries hip travel (for stride-sync drift). */
  readonly hipsSourceName: string | null;
  /** Retarget one source quaternion track's flat [x,y,z,w,…] values onto Manny.
   *  Returns null when the source bone isn't mapped (caller drops the track). */
  convert(srcBone: string, values: Float32Array): { target: string; values: Float32Array } | null;
}

const IDENTITY_Q = new THREE.Quaternion();

/**
 * Per-bone retarget constants A,B such that Lt = A·Ls·B makes the target bone match the
 * source bone's orientation-relative-to-its-bind in the bone's own frame (Rt⁻¹·Wt ==
 * Rs⁻¹·Ws — bind-pose matching; see the module header). B is identity (a pure left-
 * multiply); it's kept in the return shape so `convert` stays uniform. Pure (exported for tests).
 */
export function retargetConstants(
  Rs: THREE.Quaternion,
  Rps: THREE.Quaternion,
  Rt: THREE.Quaternion,
  Rpt: THREE.Quaternion,
): { A: THREE.Quaternion; B: THREE.Quaternion } {
  // A = Rpt⁻¹·Rt·Rs⁻¹·Rps  ⟹  Lt = A·Ls  ⟹  Rpt·Lt = Rt·Rs⁻¹·(Rps·Ls)  ⟹  Rt⁻¹·Wt = Rs⁻¹·Ws.
  const A = Rpt.clone().invert().multiply(Rt).multiply(Rs.clone().invert()).multiply(Rps);
  return { A, B: new THREE.Quaternion() };
}

interface RestEntry {
  pos: THREE.Vector3;
  quat: THREE.Quaternion;
  parentQuat: THREE.Quaternion;
  /** Parent BONE name (target/Manny side only — used by the world-mode stance match). */
  parent?: string;
}

// --- Target (Manny) rest world transforms, cached. REALIZED exactly the way the
// avatar builds the rig (buildRigBones → matrix world transforms), so the retarget
// targets the rig's ACTUAL rest, not an independently-walked approximation (MANNY_RIG's
// rounded quats make the two paths diverge ~0.04°). ---
let _mannyRest: Map<string, RestEntry> | null = null;
function mannyRest(): Map<string, RestEntry> {
  if (_mannyRest) return _mannyRest;
  const bones = MANNY_RIG.map(() => new THREE.Bone());
  MANNY_RIG.forEach((d, i) => {
    bones[i].name = d[0];
    bones[i].position.set(d[2], d[3], d[4]);
    bones[i].quaternion.set(d[5], d[6], d[7], d[8]);
    if (d[1] >= 0) bones[d[1]].add(bones[i]);
  });
  const holder = new THREE.Object3D();
  holder.add(bones[0]);
  holder.updateMatrixWorld(true);
  const m = new Map<string, RestEntry>();
  bones.forEach((b, i) => {
    const parent = MANNY_RIG[i][1];
    m.set(b.name, {
      pos: b.getWorldPosition(new THREE.Vector3()),
      quat: b.getWorldQuaternion(new THREE.Quaternion()),
      parentQuat: parent < 0 ? IDENTITY_Q.clone() : bones[parent].getWorldQuaternion(new THREE.Quaternion()),
      parent: parent < 0 ? "" : MANNY_RIG[parent][0],
    });
  });
  _mannyRest = m;
  return m;
}

let _warnedNoSkin = false;

/** Read a loaded source skeleton's REST world transforms. Prefer the authored BIND
 *  pose from a SkinnedMesh's inverse-bind matrices — FBXLoader leaves the bones posed
 *  at the clip's frame 0, so the LIVE transforms are NOT the rest. Fall back to live
 *  transforms only when there's no skinned mesh (e.g. a synthetic rig built at rest).
 *  `decompose` is used throughout so a scaled bind matrix (cm/100× units) can't leak
 *  scale into the extracted rotation. */
function readSourceRest(src: THREE.Object3D, trustLiveRestWithoutSkin = false): Map<string, RestEntry> {
  src.updateMatrixWorld(true);
  const m = new Map<string, RestEntry>();
  // A "with skin" export can carry several submeshes (body + eyelashes + clothing) that share
  // ONE skeleton; pick the richest so a partial submesh traversed first can't starve the map.
  let skinned: THREE.SkinnedMesh | undefined;
  src.traverse((o) => {
    const sm = o as THREE.SkinnedMesh;
    if (sm.isSkinnedMesh && (!skinned || sm.skeleton.bones.length > skinned.skeleton.bones.length)) skinned = sm;
  });

  if (skinned) {
    const sk = skinned.skeleton;
    const _s = new THREE.Vector3();
    const wq: THREE.Quaternion[] = [];
    const wp: THREE.Vector3[] = [];
    sk.bones.forEach((_, i) => {
      const q = new THREE.Quaternion();
      const p = new THREE.Vector3();
      sk.boneInverses[i].clone().invert().decompose(p, q, _s); // bindWorld = inverse(boneInverse)
      wq[i] = q;
      wp[i] = p;
    });
    sk.bones.forEach((b, i) => {
      const pi = sk.bones.indexOf(b.parent as THREE.Bone);
      const parentQuat = pi >= 0 ? wq[pi] : b.parent ? b.parent.getWorldQuaternion(new THREE.Quaternion()) : IDENTITY_Q.clone();
      m.set(canonName(b.name), { pos: wp[i], quat: wq[i], parentQuat }); // canonical key: a variant-prefix re-upload still resolves
    });
    return m;
  }

  // No skin → fall back to the live bone transforms. Whether those are the bind/T-pose or the
  // animation's frame-0 is export-dependent (Mixamo "Without Skin" fbx tend to retain the bind;
  // some exporters leave frame 0). It's correct here only if it's the bind pose — warn once so a
  // misaligned stance is traceable. Export WITH SKIN to read the bind pose unambiguously.
  if (!trustLiveRestWithoutSkin && !_warnedNoSkin) {
    _warnedNoSkin = true;
    console.warn("[retarget] source FBX has no SkinnedMesh — using live bone transforms as the rest; if the retargeted pose looks off, re-export WITH SKIN for an unambiguous bind pose.");
  }
  src.traverse((o) => {
    if (!(o as THREE.Bone).isBone) return;
    m.set(canonName(o.name), {
      pos: o.getWorldPosition(new THREE.Vector3()),
      quat: o.getWorldQuaternion(new THREE.Quaternion()),
      parentQuat: o.parent ? o.parent.getWorldQuaternion(new THREE.Quaternion()) : IDENTITY_Q.clone(),
    });
  });
  return m;
}

/** Normalize a Mixamo bone name's prefix to the canonical `mixamorig:` form so the map
 *  resolves regardless of export quirks: re-uploads yield `mixamorig1:` / stacked-doubled
 *  prefixes, some FBX exports DROP the colon entirely (`mixamorigHips`), and GLTFLoader
 *  rewrites the `:` to `_` (`mixamorig_Hips`) because `:` is reserved in three's track-
 *  binding path syntax — so glb-loaded Mixamo clips arrive underscored. All collapse to
 *  `mixamorig:`. Exported so the ingestion (anim.ts) folds the hips .position track the
 *  same way the quaternion path does. Non-Mixamo names (e.g. Manny "pelvis") pass through. */
export function canonName(name: string): string {
  return name.replace(/^(?:mixamorig\d*[:_]?)+/, "mixamorig:");
}

// --- auto-align: derive a cardinal-snapped source→target frame rotation ---------

/** Right-handed orthonormal basis (columns right,up,forward) from rest joint
 *  positions. up = head−hips; right = the hip line ⟂up; forward = right×up, its
 *  SIGN fixed by `forwardRef` (a facing hint — toe-vs-foot, or foot-vs-knee). Cross-
 *  products guarantee orthonormality, so the result is a proper rotation. Returns null
 *  on degenerate geometry (zero hip width, or head coincident with hips) — three's
 *  normalize() turns a zero vector into (0,0,0), which would otherwise yield a silent
 *  rank-deficient basis. */
function frameBasis(hips: THREE.Vector3, head: THREE.Vector3, thighR: THREE.Vector3, thighL: THREE.Vector3, forwardRef: THREE.Vector3 | null): THREE.Matrix3 | null {
  const up = head.clone().sub(hips);
  const r0 = thighR.clone().sub(thighL);
  if (up.lengthSq() < 1e-8 || r0.lengthSq() < 1e-8) return null; // no vertical / no hip width
  up.normalize();
  const right = r0.sub(up.clone().multiplyScalar(up.dot(r0)));
  if (right.lengthSq() < 1e-8) return null; // hip line parallel to up
  right.normalize();
  let forward = new THREE.Vector3().crossVectors(right, up).normalize(); // right×up ⇒ det +1
  if (forwardRef && forwardRef.lengthSq() > 1e-8 && forward.dot(forwardRef) < 0) {
    // Facing the wrong way: flip 180° about up (negate right AND forward → still det +1).
    right.negate();
    forward = new THREE.Vector3().crossVectors(right, up).normalize();
  }
  return new THREE.Matrix3().set(right.x, up.x, forward.x, right.y, up.y, forward.y, right.z, up.z, forward.z);
}

function det3(m: THREE.Matrix3): number {
  const e = m.elements; // column-major
  return e[0] * (e[4] * e[8] - e[5] * e[7]) - e[3] * (e[1] * e[8] - e[2] * e[7]) + e[6] * (e[1] * e[5] - e[2] * e[4]);
}

/** Snap a near-axis-aligned rotation to the exact 90° grid (a signed permutation
 *  matrix), so an FBX frame difference (always axis-aligned) cancels EXACTLY.
 *  Returns null if the snap is degenerate (two columns share an axis) or a
 *  reflection (det −1) — caller then keeps the un-snapped rotation. */
function snapRotation(m: THREE.Matrix3): THREE.Matrix3 | null {
  const e = m.elements; // column-major: col c = e[c*3 .. c*3+2]
  const out = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];
  const used = [false, false, false];
  for (let c = 0; c < 3; c++) {
    let ax = 0;
    let best = -1;
    for (let a = 0; a < 3; a++) {
      const v = Math.abs(e[c * 3 + a]);
      if (v > best) { best = v; ax = a; }
    }
    if (used[ax]) return null; // degenerate
    used[ax] = true;
    out[c][ax] = Math.sign(e[c * 3 + ax]) || 1;
  }
  const M = new THREE.Matrix3().set(out[0][0], out[1][0], out[2][0], out[0][1], out[1][1], out[2][1], out[0][2], out[1][2], out[2][2]);
  return det3(M) > 0.5 ? M : null; // reject reflections
}

function quatFromMat3(m: THREE.Matrix3): THREE.Quaternion {
  const e = m.elements; // column-major → fill a rotation Matrix4
  const m4 = new THREE.Matrix4().set(e[0], e[3], e[6], 0, e[1], e[4], e[7], 0, e[2], e[5], e[8], 0, 0, 0, 0, 1);
  return new THREE.Quaternion().setFromRotationMatrix(m4);
}

function refSource(map: RetargetMap, target: string): string | null {
  for (const [s, t] of Object.entries(map.bones)) if (t === target) return s;
  return null;
}

/** Max angle (rad, ~7°) the snapped grid rotation may sit from the raw auto-align before
 *  we DISTRUST the snap and keep the un-snapped rotation. Real FBX frame differences are
 *  axis-aligned (align lands within float noise of the grid); a large gap means the derived
 *  basis is off (stylized/asymmetric rest, noisy refs), where snapping would inject ~45°. */
const SNAP_TOL = 0.12;

/** The source→target global rotation that reconciles their rest frames. */
function computeAlign(srcRest: Map<string, RestEntry>, map: RetargetMap): THREE.Quaternion {
  const tgtRest = mannyRest();
  const identity = (): THREE.Quaternion => new THREE.Quaternion();
  // Source/target REST world position of a Manny bone (source via the map + prefix fold).
  const srcPos = (tgt: string): THREE.Vector3 | null => {
    const s = refSource(map, tgt);
    const sr = s ? srcRest.get(s) ?? srcRest.get(canonName(s)) : undefined;
    return sr ? sr.pos : null;
  };
  const tgtPos = (tgt: string): THREE.Vector3 | null => tgtRest.get(tgt)?.pos ?? null;

  // REQUIRED for the (up, right) basis. Forward is only a SIGN, derived progressively below,
  // so the toe (often dropped on stripped Mixamo exports) is NOT required.
  const sp: Record<string, THREE.Vector3> = {};
  const tp: Record<string, THREE.Vector3> = {};
  for (const tgt of ["pelvis", "head", "thigh_l", "thigh_r"]) {
    const s = srcPos(tgt);
    const t = tgtPos(tgt);
    if (!s || !t) {
      console.warn(`[retarget] auto-align missing reference bone "${tgt}" — using identity align`);
      return identity();
    }
    sp[tgt] = s;
    tp[tgt] = t;
  }
  // Forward-sign hint. The sign only needs to be CONSISTENT between the two skeletons (it
  // cancels in Mtgt·Msrcᵀ), so BOTH must use the SAME reference type — gate on the SOURCE,
  // since the target (Manny) always has a toe. Toe leads foot (ball_l−foot_l) when the source
  // has a toe; else foot-vs-knee (foot_l−calf_l) on both; else none (rely on right×up).
  const srcHasToe = !!srcPos("ball_l");
  const fwdRef = (pos: (t: string) => THREE.Vector3 | null): THREE.Vector3 | null => {
    const foot = pos("foot_l");
    if (srcHasToe) {
      const ball = pos("ball_l");
      if (ball && foot) return ball.clone().sub(foot);
    }
    const calf = pos("calf_l");
    if (foot && calf) return foot.clone().sub(calf);
    return null;
  };
  const Msrc = frameBasis(sp.pelvis, sp.head, sp.thigh_r, sp.thigh_l, fwdRef(srcPos));
  const Mtgt = frameBasis(tp.pelvis, tp.head, tp.thigh_r, tp.thigh_l, fwdRef(tgtPos));
  if (!Msrc || !Mtgt) {
    console.warn("[retarget] auto-align: degenerate rest geometry (zero hip width or head==hips) — using identity align");
    return identity();
  }
  const align3 = Mtgt.multiply(Msrc.transpose()); // Mtgt · Msrcᵀ (both orthonormal ⇒ a rotation)
  const rawQ = quatFromMat3(align3);
  const snapped = snapRotation(align3);
  if (snapped) {
    const snapQ = quatFromMat3(snapped);
    if (snapQ.angleTo(rawQ) < SNAP_TOL) return snapQ; // genuine axis-aligned diff → exact cancel
    console.warn(`[retarget] auto-align basis is ${(snapQ.angleTo(rawQ) * 57.3).toFixed(1)}° off the 90° grid — using the un-snapped align (pass an explicit map.align to override)`);
  }
  return rawQ;
}

/** WORLD-mode stance match: per-TARGET-bone swing S that re-poses the target's rest bone
 *  DIRECTION onto the source's (shortest arc), so the world-delta constants are taken against
 *  a target rest standing in the SOURCE's stance. Without it, a T-rest source driving an
 *  A-rest target applies the T→pose swing on top of the already-lowered A arms — the arms
 *  overshoot behind the body. Directions are derived from the MAPPED skeleton graph (each
 *  bone → its single mapped descendant); bones without an unambiguous direction (hand → five
 *  fingers, leaf head/toes) inherit the nearest mapped ancestor's swing so a chain never
 *  counter-rotates against its parent. Legs/spine on near-matching rigs come out ≈identity,
 *  so this reduces to plain world-delta when the stances already agree. */
function stanceSwings(
  src: THREE.Object3D,
  srcRest: Map<string, RestEntry>,
  tgtRest: Map<string, RestEntry>,
  map: RetargetMap,
  align: THREE.Quaternion,
): Map<string, THREE.Quaternion> {
  const mappedSrc = new Set(Object.keys(map.bones).map(canonName));
  const toSrc = new Map(Object.entries(map.bones).map(([s, t]) => [t, s])); // target → source

  // Nearest MAPPED ancestor per mapped target bone (walk the MANNY_RIG parent chain
  // through unmapped bones — spine_02/04, twists, metacarpals).
  const idx = new Map(MANNY_RIG.map((d, i) => [d[0], i] as const));
  const tgtAncestor = (t: string): string | null => {
    let i = MANNY_RIG[idx.get(t)!]?.[1] ?? -1;
    while (i >= 0) {
      const name = MANNY_RIG[i][0];
      if (toSrc.has(name)) return name;
      i = MANNY_RIG[i][1];
    }
    return null;
  };
  // Same on the SOURCE scene graph (consistency check for the child correspondence).
  const srcAncestor = new Map<string, string | null>();
  src.traverse((o) => {
    if (!mappedSrc.has(canonName(o.name))) return;
    let p = o.parent;
    while (p && !mappedSrc.has(canonName(p.name))) p = p.parent;
    srcAncestor.set(canonName(o.name), p ? canonName(p.name) : null);
  });

  // Mapped-children per target bone → an unambiguous bone direction needs exactly one.
  const tgtChildren = new Map<string, string[]>();
  for (const t of toSrc.keys()) {
    const a = tgtAncestor(t);
    if (a) (tgtChildren.get(a) ?? tgtChildren.set(a, []).get(a)!).push(t);
  }

  const S = new Map<string, THREE.Quaternion>();
  const ds = new THREE.Vector3();
  const dt = new THREE.Vector3();
  for (const [t, s] of toSrc) {
    const kids = tgtChildren.get(t);
    if (!kids || kids.length !== 1) continue;
    const t2 = kids[0];
    const s2 = toSrc.get(t2)!;
    // The child must correspond on BOTH graphs, or the direction pair is meaningless.
    if (srcAncestor.get(canonName(s2)) !== canonName(s)) continue;
    const sA = srcRest.get(canonName(s));
    const sC = srcRest.get(canonName(s2));
    const tA = tgtRest.get(t);
    const tC = tgtRest.get(t2);
    if (!sA || !sC || !tA || !tC) continue;
    ds.copy(sC.pos).sub(sA.pos).applyQuaternion(align);
    dt.copy(tC.pos).sub(tA.pos);
    if (ds.lengthSq() < 1e-10 || dt.lengthSq() < 1e-10) continue;
    ds.normalize();
    dt.normalize();
    if (ds.dot(dt) < -0.999) continue; // antiparallel: shortest arc is ill-defined — skip
    S.set(t, new THREE.Quaternion().setFromUnitVectors(dt, ds));
  }
  // EXACT palm frames: a hand has five mapped children (no single direction), so the generic
  // rule would hand it the forearm's swing — which fixes the wrist DIRECTION but leaves a
  // constant palm ROLL error (~20° measured on the UAL packs) that the procedural twist
  // driver then spreads down the forearm (candy-wrap pinching). Both rigs map the finger
  // roots, so build the full two-axis palm frame on each skeleton — knuckles (hand→middle
  // root) × across-palm (index→pinky root) — and take the exact frame-to-frame rotation.
  for (const l of ["l", "r"]) {
    const hand = `hand_${l}`;
    const joints = [hand, `middle_01_${l}`, `index_01_${l}`, `pinky_01_${l}`];
    const sj = joints.map((j) => { const s2 = toSrc.get(j); return s2 ? srcRest.get(canonName(s2)) : undefined; });
    const tj = joints.map((j) => tgtRest.get(j));
    if (sj.some((e) => !e) || tj.some((e) => !e)) continue;
    const palm = (e: (RestEntry | undefined)[], alignQ: THREE.Quaternion | null): THREE.Matrix3 | null => {
      const z = e[1]!.pos.clone().sub(e[0]!.pos); // knuckle direction
      const y0 = e[2]!.pos.clone().sub(e[3]!.pos); // across the palm (index→pinky — same sign per side on both rigs)
      if (alignQ) { z.applyQuaternion(alignQ); y0.applyQuaternion(alignQ); }
      if (z.lengthSq() < 1e-10) return null;
      z.normalize();
      const y = y0.sub(z.clone().multiplyScalar(z.dot(y0)));
      if (y.lengthSq() < 1e-10) return null;
      y.normalize();
      const x = new THREE.Vector3().crossVectors(y, z); // orthonormal by construction
      return new THREE.Matrix3().set(x.x, y.x, z.x, x.y, y.y, z.y, x.z, y.z, z.z);
    };
    const Ms = palm(sj, align);
    const Mt = palm(tj, null);
    if (!Ms || !Mt) continue;
    S.set(hand, quatFromMat3(Ms.multiply(Mt.transpose()))); // target palm frame → source palm frame
  }
  // Inherit: direction-less bones ride the nearest mapped ancestor's swing.
  for (const t of toSrc.keys()) {
    if (S.has(t)) continue;
    for (let a = tgtAncestor(t); a; a = tgtAncestor(a)) {
      const sa = S.get(a);
      if (sa) { S.set(t, sa.clone()); break; }
    }
  }
  return S;
}

/** Build a clip-ready retargeter: snapshot both rests, resolve the align, and bake
 *  the per-bone A,B constants. Reused for every track of the clip. */
export function buildRetargeter(src: THREE.Object3D, map: RetargetMap): Retargeter {
  const srcRest = readSourceRest(src, map.trustLiveRestWithoutSkin);
  const tgtRest = mannyRest();
  const align = map.align && map.align !== "auto" ? map.align.clone() : computeAlign(srcRest, map);
  const stance = map.mode === "world" ? stanceSwings(src, srcRest, tgtRest, map, align) : null;

  const conv = new Map<string, { target: string; A: THREE.Quaternion; B: THREE.Quaternion }>();
  for (const [s, t] of Object.entries(map.bones)) {
    const sr = srcRest.get(s) ?? srcRest.get(canonName(s));
    const tr = tgtRest.get(t);
    if (!sr || !tr) continue; // bone absent in this clip's skeleton, or unknown target
    const Rs = align.clone().multiply(sr.quat); // align·Rs_raw → target frame
    const Rps = align.clone().multiply(sr.parentQuat);
    let A: THREE.Quaternion;
    let B: THREE.Quaternion;
    if (map.mode === "world") {
      // WORLD mode: reproduce the source's ABSOLUTE pose — Wt = Ws·K, K = Rs⁻¹·Rt' constant,
      // with Rt' = S·Rt the STANCE-MATCHED target rest (stanceSwings). Lt = Wtₚ⁻¹·Wt expands
      // to A = Rt'ₚ⁻¹·Rps, B = Rs⁻¹·Rt' — still one A·Ls·B per bone.
      //
      // ⚠ Sp MUST be the swing of the nearest STANCE-CARRYING ancestor, walked through any
      // UNMAPPED intermediates — not `stance.get(tr.parent) else identity`. The parent's rest
      // world AS THE TRANSFER PRODUCES IT is (S_nearestMapped · Rt_parent): an unmapped
      // in-between bone (spine_02/spine_04 on this map) rides its rest local UNDER its
      // transferred ancestor, so the ancestor's swing is still in the frame. Looking up only
      // the literal parent made that swing fail to cancel — it leaked (conjugated) into
      // every bone below the gap: measured 8–13° of arm error on every UAL clip while the
      // gap-free legs sat at 0.0° (_retargetoracle.mts, the tell that found this).
      let Sp: THREE.Quaternion = IDENTITY_Q;
      for (let p = tr.parent; p; p = tgtRest.get(p)?.parent) {
        const sw = stance!.get(p);
        if (sw) { Sp = sw; break; }
      }
      const Sb = stance!.get(t) ?? IDENTITY_Q;
      A = tr.parentQuat.clone().premultiply(Sp).invert().multiply(Rps);
      B = Rs.clone().invert().multiply(tr.quat.clone().premultiply(Sb));
    } else {
      ({ A, B } = retargetConstants(Rs, Rps, tr.quat, tr.parentQuat));
    }
    conv.set(s, { target: t, A, B });
  }

  const hipsSourceName = refSource(map, "pelvis");
  const _q = new THREE.Quaternion();
  return {
    align,
    hipsSourceName,
    convert(srcBone, values) {
      const c = conv.get(srcBone) ?? conv.get(canonName(srcBone));
      if (!c) return null;
      const out = new Float32Array(values.length);
      for (let i = 0; i < values.length; i += 4) {
        _q.set(values[i], values[i + 1], values[i + 2], values[i + 3]).premultiply(c.A).multiply(c.B).normalize();
        out[i] = _q.x;
        out[i + 1] = _q.y;
        out[i + 2] = _q.z;
        out[i + 3] = _q.w;
      }
      return { target: c.target, values: out };
    },
  };
}

// --- The Mixamo → Manny bone map ----------------------------------------------
// Standard 65-bone Adobe Mixamo humanoid (prefix "mixamorig:"), Y-up / +Z-forward,
// cm. Mixamo has only 3 spine segments and NO twist/metacarpal bones, so we map
// Spine/Spine1/Spine2 → spine_01/03/05 (matching avatar.ts BONE_ALIAS) and the
// finger *1/2/3 straight onto Manny's *_01/02/03 — Manny's metacarpals, twist
// bones and unmapped middle spine just ride their rest pose. Leaf "…End"/"…4" tips
// carry no animation and are intentionally absent.
function side(s: "Left" | "Right", l: "l" | "r"): Record<string, string> {
  const f = (finger: string, mannyFinger: string): Record<string, string> => ({
    [`mixamorig:${s}Hand${finger}1`]: `${mannyFinger}_01_${l}`,
    [`mixamorig:${s}Hand${finger}2`]: `${mannyFinger}_02_${l}`,
    [`mixamorig:${s}Hand${finger}3`]: `${mannyFinger}_03_${l}`,
  });
  return {
    [`mixamorig:${s}Shoulder`]: `clavicle_${l}`,
    [`mixamorig:${s}Arm`]: `upperarm_${l}`,
    [`mixamorig:${s}ForeArm`]: `lowerarm_${l}`,
    [`mixamorig:${s}Hand`]: `hand_${l}`,
    [`mixamorig:${s}UpLeg`]: `thigh_${l}`,
    [`mixamorig:${s}Leg`]: `calf_${l}`,
    [`mixamorig:${s}Foot`]: `foot_${l}`,
    [`mixamorig:${s}ToeBase`]: `ball_${l}`,
    // Thumb has no metacarpal on either skeleton → 1/2/3 map directly.
    [`mixamorig:${s}HandThumb1`]: `thumb_01_${l}`,
    [`mixamorig:${s}HandThumb2`]: `thumb_02_${l}`,
    [`mixamorig:${s}HandThumb3`]: `thumb_03_${l}`,
    ...f("Index", "index"),
    ...f("Middle", "middle"),
    ...f("Ring", "ring"),
    ...f("Pinky", "pinky"),
  };
}

export const MIXAMO_TO_MANNY: RetargetMap = {
  // Mixamo and the Manny rig are BOTH Y-up / +Z-forward, so the global frame reconciliation
  // is exactly identity — pinned, not auto-derived. (Geometric auto-align measures the
  // T-pose-vs-A-pose limb splay as a ~14° "frame" tilt and gets it wrong; FK world-delta
  // confirms identity reproduces the source motion to 0.00°, auto to 17°.) The per-bone
  // retarget handles the T→A rest difference.
  align: new THREE.Quaternion(),
  bones: {
    "mixamorig:Hips": "pelvis",
    "mixamorig:Spine": "spine_01",
    "mixamorig:Spine1": "spine_03",
    "mixamorig:Spine2": "spine_05",
    "mixamorig:Neck": "neck_01",
    "mixamorig:Head": "head",
    ...side("Left", "l"),
    ...side("Right", "r"),
  },
};

// --- The Quaternius "Universal Animation Library" → Manny bone map -------------
// The UAL "Unreal-Godot" GLBs use UE-Manny bone NAMES but are a DIFFERENT skeleton: a
// distinct bind orientation (rest 56–180° off Manny), different proportions, only 3 spine
// bones (spine_01/02/03 → spine_01/03/05, exactly the Mixamo case above), and a capital
// "Head". So they are FOREIGN and MUST be retargeted, not bound natively (native binding
// applies their raw locals onto our rig → mangled). buildRetargeter reads the source bind
// from the GLB's SkinnedMesh; the per-bone rest reconciliation lands the motion correctly.
function ualSide(l: "l" | "r"): Record<string, string> {
  const f = (finger: string): Record<string, string> => ({
    [`${finger}_01_${l}`]: `${finger}_01_${l}`,
    [`${finger}_02_${l}`]: `${finger}_02_${l}`,
    [`${finger}_03_${l}`]: `${finger}_03_${l}`,
  });
  return {
    [`clavicle_${l}`]: `clavicle_${l}`,
    [`upperarm_${l}`]: `upperarm_${l}`,
    [`lowerarm_${l}`]: `lowerarm_${l}`,
    [`hand_${l}`]: `hand_${l}`,
    [`thigh_${l}`]: `thigh_${l}`,
    [`calf_${l}`]: `calf_${l}`,
    [`foot_${l}`]: `foot_${l}`,
    [`ball_${l}`]: `ball_${l}`,
    [`thumb_01_${l}`]: `thumb_01_${l}`,
    [`thumb_02_${l}`]: `thumb_02_${l}`,
    [`thumb_03_${l}`]: `thumb_03_${l}`,
    ...f("index"), ...f("middle"), ...f("ring"), ...f("pinky"),
  };
}

export const UAL_TO_MANNY: RetargetMap = {
  // Y-up / +Z-forward like Manny, so the global frame reconciliation is identity (pinned).
  align: new THREE.Quaternion(),
  // WORLD mode: these are Blender/Rigify-derived bones (UE-Manny NAMES only — +Y-down-the-bone
  // local axes AND a T-pose rest, vs our +X convention / A-pose rest). Bind-matching rotates
  // about the wrong local axis (motion tips the body); world mode reproduces the source's
  // absolute pose, with its stance match absorbing the T-vs-A rest difference (raw world-delta
  // sent the arms swinging BEHIND the body).
  mode: "world",
  bones: {
    pelvis: "pelvis",
    spine_01: "spine_01",
    spine_02: "spine_03",
    spine_03: "spine_05",
    neck_01: "neck_01",
    Head: "head",
    ...ualSide("l"),
    ...ualSide("r"),
  },
};
